import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Route guard that checks if user has completed Tailspin onboarding
 * Redirects to onboarding if not completed
 */
export default function TailspinOnboardingGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Paths that don't require onboarding check
  const publicPaths = [
    "/login",
    "/callback",
    "/tailspin/onboarding/step1",
    "/tailspin/onboarding/step2",
    "/tailspin/onboarding/step3",
    "/tailspin/dashboard" // Allow dashboard access - it will show product tour if needed
  ];

  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

  if (isPublicPath) {
    return children;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Checking onboarding status...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If not onboarded and trying to access protected routes (not dashboard/onboarding), 
  // redirect to dashboard where product tour will show
  if (!user.isTailspinOnboarded && !user.needsOnboarding) {
    return <Navigate to="/tailspin/dashboard" replace />;
  }

  // If user explicitly needs onboarding (new user), redirect to onboarding step 1
  if (user.needsOnboarding) {
    return <Navigate to="/tailspin/onboarding/step1" replace />;
  }

  return children;
}

